import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Order } from '../types/BookingTypes';
import { getCustomerOrders } from '../services/orderService';
import { getCurrentUser } from '../services/authService';
import Header from '../components/Header';

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const user = getCurrentUser();
        if (!user) {
          navigate('/login?returnUrl=/orders');
          return;
        }

        const fetchedOrders = await getCustomerOrders(user.phoneNumber);
        setOrders(fetchedOrders);
      } catch (err) {
        setError('Failed to fetch orders. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [navigate]);

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'CREATED':
        return 'bg-blue-50 text-blue-700 border border-blue-100';
      case 'CONFIRMED':
        return 'bg-green-50 text-green-700 border border-green-100';
      case 'IN_PROGRESS':
        return 'bg-yellow-50 text-yellow-700 border border-yellow-100';
      case 'COMPLETED':
        return 'bg-green-50 text-green-700 border border-green-100';
      case 'CANCELLED':
        return 'bg-red-50 text-red-700 border border-red-100';
      default:
        return 'bg-gray-50 text-gray-700 border border-gray-100';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '0001-01-01T00:00:00') return '';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="My Orders" />

      <div className="container mx-auto px-4 py-4">
        {/* Browse Services Button - Always visible */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="w-full px-4 py-3 rounded-xl font-medium text-base text-minto-600 bg-white border border-minto-200 hover:bg-minto-50 active:bg-minto-100 transition-colors shadow-sm hover:shadow-md flex items-center justify-center"
          >
            <i className="fas fa-tools mr-2"></i>
            Browse Services
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-minto-500"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="text-minto-600 hover:text-minto-700"
            >
              Try Again
            </button>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 text-gray-300">
              <i className="fas fa-box-open text-6xl"></i>
            </div>
            <h2 className="text-xl font-medium text-gray-600 mb-2">No orders found</h2>
            <p className="text-gray-500 mb-6">You haven't placed any orders yet</p>
            <p className="text-gray-500">Use the "Browse Services" button above to get started!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <div 
                key={order.id} 
                onClick={() => navigate(`/order-summary/${order.id}`, { state: { order } })}
                className="bg-white rounded-xl p-4 shadow-sm active:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm font-medium text-gray-900 truncate">{order.orderNumber}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-mobile-alt text-gray-400"></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 font-medium truncate">
                          {order.makeName} {order.modelName}
                        </p>
                        {formatDate(order.orderDate) && (
                          <p className="text-xs text-gray-500">
                            {formatDate(order.orderDate)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-base font-semibold text-minto-600">
                      ₹{Math.round(order.finalAmount)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${getStatusColor(order.orderStatus)}`}>
                    {order.orderStatus}
                  </span>
                  <span className="text-xs text-minto-600 flex items-center">
                    View Details
                    <i className="fas fa-chevron-right text-xs ml-1"></i>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;